import {
  Organization,
  OrganizationManagerNotification,
  TeamManager,
} from "@entities";
import { extender } from "@shared/backend/class-extender";
import HyperEntity from "@shared/backend/hyper-entity.decorator";
import { GENDER_LITERAL, GENDER_TO_HEB } from "@shared/helpers/constants";
import { JsonField } from "@shared/helpers/custom-fields";
import {
  isAdminsOrCurrentUser,
  isSuperAdminOrCurrentUser,
} from "@shared/helpers/include-in-api.function";
import { BaseRoles, Roles } from "@shared/Roles";
import { Fields, Relations, remult, Validators } from "remult";
import { OrganizationManagerEntityConfig } from "./config";
import { OrganizationManagerFilters } from "./filters";
import { OrganizationManagerMethods } from "./methods";

@HyperEntity("organization-manager", OrganizationManagerEntityConfig)
export class OrganizationManager extends extender(
  OrganizationManagerFilters,
  OrganizationManagerMethods,
) {
  @Fields.id()
  id = "";

  @Relations.toOne(() => Organization)
  organization?: Organization;

  @Relations.toMany(() => OrganizationManagerNotification, "toId")
  notifications: OrganizationManagerNotification[] = [];

  @Relations.toMany(() => TeamManager, {
    field: "managerId",
    caption: "קבוצות",
  })
  teams?: TeamManager[];

  @Fields.string({ caption: "שם מלא" })
  name = "";

  @Fields.string({
    dbName: "phone_number",
    validate: [Validators.unique],
    caption: "מספר טלפון",
    allowApiUpdate: false,
    valueConverter: {
      displayValue(val) {
        // Replace the country code with "0"
        let formattedVal = val.replace("+972", "0");

        // Format the phone number as XXX-XXX-XXXXX
        formattedVal = formattedVal.replace(
          /^(\d{3})(\d{3})(\d+)$/,
          "$1-$2-$3",
        );

        return formattedVal;
      },
    },
  })
  phoneNumber = "";

  @JsonField({
    allowApiUpdate: () =>
      remult.isAllowed(Roles.SuperDuperAdmin) ? true : false,
    includeInApi: isSuperAdminOrCurrentUser,
    caption: "הרשאות",
  })
  roles: BaseRoles[] = [Roles.Manager];

  @Fields.boolean({
    dbName: "is_mute",
    includeInApi: isSuperAdminOrCurrentUser,
  })
  isMute = false;

  @Fields.boolean({
    dbName: "is_call_center_station",
    includeInApi: Roles.AdminsAndManager,
    caption: "עמדת מוקד",
  })
  isCallCenterStation = false;

  @Fields.integer({
    dbName: "station_number",
    allowNull: true,
    includeInApi: Roles.AdminsAndManager,
    caption: "מספר עמדה",
  })
  stationNumber?: number;

  @Fields.string({
    dbName: "profile_image_id",
  })
  profileImageId = "";

  @Fields.literal(() => GENDER_LITERAL, {
    caption: "מגדר",
    includeInApi: isSuperAdminOrCurrentUser,
    validate: (_, event) => {
      return GENDER_LITERAL.includes(event.value);
    },
    displayValue: (_, value) => GENDER_TO_HEB[value],
  })
  gender: (typeof GENDER_LITERAL)[number] = "OTHER";

  @Fields.string({ dbName: "email", includeInApi: isSuperAdminOrCurrentUser })
  email = "";

  @Fields.string({
    dbName: "role",
    caption: "תפקיד",
  })
  role = "";

  /** מזהה רגל 1 ב־Voicenter Click2Call - נשלח כ־`phone` (שלוחה / קוד עובד / מזהה מהממשק). */
  @Fields.string({
    dbName: "voicenter_extension",
    allowNull: true,
    caption: "Voicenter - מזהה Click2Call (phone)",
    includeInApi: isAdminsOrCurrentUser,
  })
  voicenterExtension?: string | null;

  /** שדה ישן; Click2Call לא משתמש בו (רק `voicenterExtension` → `phone`). */
  @Fields.string({
    dbName: "voicenter_phone_login_code",
    allowNull: true,
    caption: "קוד אישי Voicenter (לא בשימוש ב־Click2Call)",
    includeInApi: isAdminsOrCurrentUser,
  })
  voicenterPhoneLoginCode?: string | null;

  @Fields.date({
    includeInApi: false,
    dbName: "last_notification_reminder_date",
  })
  lastNotificationReminderDate = new Date();

  @Fields.date({ includeInApi: Roles.AdminsAndManager, dbName: "last_entry" })
  lastEntry = new Date();

  @Fields.createdAt({
    includeInApi: isSuperAdminOrCurrentUser,
    dbName: "created_at",
  })
  createdAt = new Date();

  @Fields.updatedAt({
    includeInApi: isSuperAdminOrCurrentUser,
    dbName: "updated_at",
  })
  updatedAt = new Date();
}
